import { EventConfig, EventFactory, ISemanticEvent, createEventFactory } from '@cp/shared/tracking';
import { useCallback, useMemo } from 'react';
import { useEventBus } from 'src/shared/eventBus';
import { withTrack } from './withTrack';

export const useTrackEvent = <T>() => {
  const eventBus = useEventBus();
  return useCallback(
    (event: ISemanticEvent<T>) => {
      eventBus.publish(event);
    },
    [eventBus],
  );
};

/** return callback that send semantic event to trackers */
export const useTracking = <T = void, M = T>(eventFactory: EventFactory<T, M> | EventConfig) => {
  const track = useTrackEvent();
  return useCallback(
    (payload: M) => {
      const event = typeof eventFactory === 'function' ? eventFactory(payload) : createEventFactory<T, M>(eventFactory)(payload);
      track(event);
    },
    [eventFactory, track],
  );
};

/**
 * Create decorator with firing semantic event before trigger original function
 * @example ```jsx
 * const event = createEventFactory({ name: 'my-event' });
 * const withTrack = useWithTrack();
 * const onSubmit = (e) => { e.preventDefault(); }
 * return <form onSubmit={withTrack(event, onSubmit)}></form>
 * ```
 */
export const useWithTrack = () => {
  const track = useTrackEvent();
  return useMemo(() => withTrack.bind(null, track), [track]) as unknown as <T extends (...args: never[]) => any, EP = void>(
    eb: (...v: Parameters<T>) => ISemanticEvent<EP>,
    fn: T,
  ) => (...a: Parameters<T>) => ReturnType<T>;
};
